'use client';

import { Typography, type SxProps, type Theme } from '@mui/material';
import { CatalogCard } from '@odys/design-system/components/CatalogCard';
import { ImageCarousel } from '@odys/design-system/components/ImageCarousel';
import { useLocale, useTranslations } from 'next-intl';

import type { ItineraryCardDto } from '@/app/api/lib/itineraries/itinerary.types';
import type { Role } from '@/app/api/lib/permissions/matrix';
import { RegionChips } from '@/components/common/catalog/RegionChips';
import { IncompleteBadge } from '@/components/common/IncompleteBadge';
import { ceilAmount, formatPrice } from '@/components/common/price-format';
import { VisitorOverlay } from '@/components/common/VisitorOverlay';

type ItineraryT = ReturnType<typeof useTranslations<'dmcItinerariesPage'>>;

/**
 * Ligne de métrique d'une carte Itinéraire : « {jours} jours · {type} » pour un
 * viewer connecté ayant un type de voyage, « {jours} jours » sinon, et `null`
 * (ligne omise) quand le nombre de jours est inconnu. Pas de nombre de destinations.
 */
export function buildDurationLine(
    item: ItineraryCardDto,
    viewerRole: Role,
    t: ItineraryT,
): string | null {
    if (item.numberDays === null) return null;
    if (viewerRole !== 'visitor' && item.travelType !== null) {
        return t('durationTypePattern', {
            numberDays: item.numberDays,
            travelType: item.travelType,
        });
    }
    return t('durationPattern', { numberDays: item.numberDays });
}

/**
 * Prix PAR VOYAGEUR d'un itinéraire type : « 2 518 € / pers. ».
 *
 * La source Mirage ne connaît qu'un total de devis (`price.amount`) valable
 * pour un effectif donné (`price.pax`) ; le per-pax est dérivé ici, dans la
 * couche présentation, pour que le DTO reste fidèle à la source et que ce choix
 * d'affichage puisse changer sans toucher au read model.
 *
 * Arrondi à l'entier SUPÉRIEUR (`ceilAmount`, convention #2095) : mieux vaut
 * annoncer un euro de trop qu'un euro de moins sur un prix commercial.
 *
 * Rien n'est rendu dans trois cas : pas de prix du tout (DMC qui n'a pas
 * chiffré), Visiteur (prix amputé à la source, cf. `itinerary.mapper`), et
 * effectif inconnu ou nul — le per-pax n'est alors pas calculable, et afficher
 * le total de groupe à la place se lirait comme un prix par personne, soit
 * exactement l'erreur qu'on cherche à éviter.
 *
 * La devise reste celle du devis : la source ne fournit pas de double montant
 * EUR/USD comme les Produits, donc le sélecteur de la navbar ne s'applique pas.
 */
export function ItineraryPriceLine({
    price,
    t,
    sx,
}: {
    price: ItineraryCardDto['price'];
    t: ItineraryT;
    sx?: SxProps<Theme>;
}) {
    const locale = useLocale();
    // Montant nul/négatif = prix inconnu → pas de ligne, jamais « 0 € / pers. ».
    if (!price || price.amount <= 0 || price.pax === null || price.pax <= 0) return null;

    const amount = formatPrice(ceilAmount(price.amount / price.pax), price.currency, locale, {
        maximumFractionDigits: 0,
    });
    const label = t('pricePerTravellerPattern', { amount });

    return (
        <Typography
            variant="subtitle2"
            component="p"
            data-testid="itinerary-price"
            sx={[{ fontWeight: 600 }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
            {label}
        </Typography>
    );
}

type ItineraryCardProps = {
    item: ItineraryCardDto;
    viewerRole: Role;
    /** testid de la carte (spécifique à la surface : fiche vs listing). */
    testId?: string;
    /** Active l'overlay d'invitation Visiteur (listing). Défaut : false. */
    showVisitorOverlay?: boolean;
    /** Appelé quand la carte ouvre l'itinéraire dans Odys. */
    onOpen?: () => void;
};

/**
 * Carte Itinéraire unique partagée par la section « Itinéraires proposés » de la
 * fiche DMC et le listing (vue cartes). Comportement identique : toute la carte
 * est le hand-off Odys (`editInOdysUrl`, statique si null, toujours null pour
 * Visiteur), carousel pour les connectés / image statique pour le Visiteur,
 * badge « incomplet » data-driven, ligne de métrique sans nombre de destinations.
 */
export function ItineraryCard({
    item,
    viewerRole,
    testId,
    showVisitorOverlay = false,
    onOpen,
}: ItineraryCardProps) {
    const t = useTranslations('dmcItinerariesPage');
    const tImageCarousel = useTranslations('imageCarousel');

    // Le Visiteur voit une couverture figée : le survol lui révèle l'invitation
    // à s'inscrire, une navigation de carousel n'aurait rien à lui apprendre.
    const isVisitor = viewerRole === 'visitor';

    const card = (
        <CatalogCard
            data-testid={testId}
            href={item.editInOdysUrl}
            onClick={onOpen}
            dimmed={item.isIncomplete}
            title={item.templateName ?? '—'}
            meta={buildDurationLine(item, viewerRole, t)}
            media={
                <ImageCarousel
                    id={item.id}
                    imageUrls={isVisitor ? item.imageUrls.slice(0, 1) : item.imageUrls}
                    interactive={!isVisitor}
                    prevLabel={tImageCarousel('prev')}
                    nextLabel={tImageCarousel('next')}
                />
            }
            badge={
                item.isIncomplete ? (
                    <IncompleteBadge
                        label={t('card.incompleteBadge')}
                        data-testid="dmc-itineraries-incomplete-badge"
                    />
                ) : undefined
            }
            chips={
                item.regions.length > 0 ? (
                    <RegionChips regions={item.regions} data-testid="itinerary-regions" />
                ) : undefined
            }
            price={<ItineraryPriceLine price={item.price} t={t} />}
        />
    );

    if (showVisitorOverlay && viewerRole === 'visitor') {
        return (
            <VisitorOverlay
                message={t('visitorOverlay.message')}
                ctaLabel={t('visitorOverlay.cta')}
                testIdPrefix="dmc-itineraries-visitor-overlay"
            >
                {card}
            </VisitorOverlay>
        );
    }
    return card;
}
